import { useCallback, useEffect, useState } from 'react'

/**
 * Thème de l'interface. `clair` est le thème bureautique de préparation de
 * grille ; `antenne` est la variante sombre pour la régie en exploitation.
 *
 * Le basculement se contente de poser `data-theme` sur <html> : toutes les
 * couleurs viennent de tokens.css, aucun composant n'a besoin de le savoir.
 */
export type Theme = 'clair' | 'antenne'

const STORAGE_KEY = 'trafficplay:theme'

function readStoredTheme(): Theme {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'antenne' ? 'antenne' : 'clair'
  } catch {
    return 'clair'
  }
}

function applyTheme(theme: Theme) {
  if (theme === 'antenne') document.documentElement.setAttribute('data-theme', 'antenne')
  else document.documentElement.removeAttribute('data-theme')
}

// Appliqué au chargement du module, avant le premier rendu React : évite le
// flash de thème clair quand l'opérateur travaille en sombre.
applyTheme(readStoredTheme())

export function useTheme(): { theme: Theme; setTheme: (theme: Theme) => void; toggleTheme: () => void } {
  const [theme, setThemeState] = useState<Theme>(readStoredTheme)

  useEffect(() => {
    applyTheme(theme)
    try {
      localStorage.setItem(STORAGE_KEY, theme)
    } catch {
      // Mode privé ou stockage plein : le thème reste valable pour la session.
    }
  }, [theme])

  const setTheme = useCallback((next: Theme) => setThemeState(next), [])
  const toggleTheme = useCallback(() => setThemeState(current => (current === 'antenne' ? 'clair' : 'antenne')), [])

  return { theme, setTheme, toggleTheme }
}
